import React from "react";
import { Card, CardContent } from "./ui/card";
import { Heart, Users, Shield, Activity, Brain, Smile } from "lucide-react";

const DifferentlySection = () => {
  const differentiators = [
    {
      icon: Heart,
      title: "Person-Centered Care",
      description: "We don't just treat conditions - we treat people. Every program is tailored to individual needs, preferences, and life goals."
    },
    {
      icon: Users,
      title: "Community Integration",
      description: "Our approach builds social connections alongside physical health, creating a supportive community that motivates and inspires."
    },
    {
      icon: Shield,
      title: "Preventive Focus",
      description: "Rather than waiting for problems to occur, we proactively prevent falls, frailty, and decline through evidence-based interventions."
    },
    {
      icon: Activity,
      title: "Specialist Network",
      description: "We work closely with a wide network of specialists including Neurologists, Geriatricians, Orthopaedic Surgeons, Pain Specialists, and other healthcare professionals to provide comprehensive care."
    },
    {
      icon: Brain,
      title: "Evidence-Based Practice",
      description: "Every program is grounded in the latest research on aging, exercise physiology, and rehabilitation science."
    },
    {
      icon: Smile,
      title: "Joy in Movement",
      description: "We make exercise enjoyable and sustainable, focusing on activities that bring pleasure and accomplishment to daily life."
    }
  ];

  return (
    <section id="differently" className="py-24 bg-gradient-to-b from-black via-gray-900/80 to-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Main Statement */}
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-light leading-tight mb-12">
              <span className="block mb-4">We do retirement villages</span>
              <span className="text-orange-500 animate-pulse">differently</span>
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <p className="text-xl md:text-2xl text-white/80 leading-relaxed font-light mb-8">
                While others focus on managing decline, we focus on promoting growth, 
                independence, and vitality in every resident we serve.
              </p>
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto"></div>
            </div>
          </div>

          {/* Community Photo */}
          <div className="mb-20">
            <div className="relative rounded-lg overflow-hidden max-w-5xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/68v0v2oz_AWB%20Photo.jpg"
                alt="Reignite Health physiotherapist with retirement village residents during a community wellness session"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-white text-lg md:text-xl font-light text-center">
                  Building genuine connections with our retirement village communities
                </p>
              </div>
            </div>
          </div>

          {/* Key Differentiators */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {differentiators.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card
                  key={index}
                  className="bg-black/40 border-white/10 hover:border-orange-500/30 transition-all duration-500 hover:scale-105 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-full group-hover:bg-orange-500/20 transition-all duration-300 mb-4">
                        <IconComponent className="w-8 h-8 text-orange-500" />
                      </div>
                      <h3 className="text-xl font-light text-white group-hover:text-orange-100 transition-colors duration-300">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-white/70 leading-relaxed font-light">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Philosophy Statement */}
          <div className="bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10 rounded-lg p-12 border border-orange-500/20 mb-20">
            <div className="text-center">
              <h3 className="text-3xl md:text-4xl font-light mb-8 text-white">
                Our Philosophy: <span className="text-orange-500">Independence Through Movement</span>
              </h3>
              <p className="text-lg text-white/80 leading-relaxed font-light max-w-4xl mx-auto mb-8">
                Traditional retirement care often focuses on managing limitations. We believe in expanding possibilities. 
                Through specialised physiotherapy, exercise physiology, and community wellness programmes, we help residents 
                not just maintain their current abilities, but often exceed what they thought was possible.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-light text-orange-500 mb-2">98%</div>
                  <p className="text-white/70 text-sm font-light">Resident Satisfaction</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-orange-500 mb-2">99%</div>
                  <p className="text-white/70 text-sm font-light">Improved Balance Scores</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-light text-orange-500 mb-2">90%</div>
                  <p className="text-white/70 text-sm font-light">Improved Strength Scores</p>
                </div>
              </div>
            </div>
          </div>

          {/* Family-like Relationships Section */}
          <div className="mb-16">
            <div className="relative rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/dbu0ib4h_S%26amp_L_PRINT_498.jpg"
                alt="Wedding photo showing Reignite Health directors surrounded by retirement village residents who attended their special day"
                className="w-full h-auto object-cover filter grayscale hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="text-center">
                  <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
                    We treat our clients like <span className="text-orange-500">family</span>
                  </h3>
                  <p className="text-lg text-white/90 leading-relaxed font-light max-w-3xl mx-auto">
                    So much so they had to be there for the directors' big day!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Call to Action */}
          <div className="text-center mt-16">
            <p className="text-white/80 text-lg mb-6 font-light">
              Experience the <span className="text-orange-500">difference</span> for yourself
            </p>
            <button 
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-sm font-light transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-orange-500/25"
              onClick={() => {
                const element = document.querySelector("#about");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Learn More About Our Approach
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DifferentlySection;